import { Component, computed, inject, input, signal, output, effect } from '@angular/core';
import { ExplanationComponent } from '../../core/visual/explanation/explanation.component';
import { FlexModule } from '@angular/flex-layout';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatFormField } from '@angular/material/form-field';
import { MatFormFieldFix } from '../../editors/mat-form-field/mat-form-field-fix';
import { MatOption } from '@angular/material/core';
import { MatOptgroup } from '@angular/material/core';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { PhoneNumberPipe } from '../../core/pipes/phone-number.pipe';
import { MatBadge } from '@angular/material/badge';
import { FormsModule } from '@angular/forms';
import { ProMessagingService } from '../pro-messaging.service';
import { SmsPhoneNumberModel } from '../../api/models/sms-phone-number-model';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { HelpscoutService } from '../../core/services/helpscout.service';

@Component({
  selector: 'app-pro-phone-number-select',
  standalone: true,
  imports: [
    ExplanationComponent,
    FlexModule,
    MatButtonToggle,
    MatButtonToggleGroup,
    MatFormField,
    MatFormFieldFix,
    MatOption,
    MatOptgroup,
    MatSelect,
    MatSelectTrigger,
    PhoneNumberPipe,
    MatBadge,
    FormsModule,
  ],
  templateUrl: './phone-number-select.component.html',
  styleUrl: './phone-number-select.component.scss',
})
export class PhoneNumberSelectComponent {
  hasAssignUserAccess = input(false);
  dialog: MatDialog = inject(MatDialog);
  router = inject(Router);
  messagingService: ProMessagingService = inject(ProMessagingService);
  supportService = inject(HelpscoutService);
  private readonly TAG = '[PhoneNumberSelectComponent]';

  // Used by mat-select to compare options with the current selection by phoneNumber
  comparePhoneNumber = (a: SmsPhoneNumberModel | null, b: SmsPhoneNumberModel | null) =>
    (a?.phoneNumber ?? null) === (b?.phoneNumber ?? null);

  // User-chosen filter preference
  uiNumbersFilter = signal<'send' | 'receive' | 'all'>('send');
  // Tracks if the user has manually chosen a filter; if true, we respect uiNumbersFilter regardless of the selected number
  userForcedFilter = signal<boolean>(false);
  // The effective filter shown and applied; forced to 'all' if the selected number cannot send
  viewNumbersFilter = computed<'send' | 'receive' | 'all'>(() => {
    const selected = this.messagingService.selectedMessagingNumberSig();
    if (selected && selected.allowSend === false && !this.userForcedFilter()) {
      return 'all';
    }
    return this.uiNumbersFilter();
  });

  showNumbersFilter = computed(() => {
    return this.allMessagingNumbers().some(m => m.allowSend) && this.allMessagingNumbers().some(m => !m.allowSend);
  });

  filteredMessagingNumbers = computed(() => {
    const numbers = this.allMessagingNumbers();
    const selected = this.messagingService.selectedMessagingNumberSig();

    let result: SmsPhoneNumberModel[];
    const filter = this.viewNumbersFilter();
    if (filter === 'send') {
      result = numbers.filter(m => m.allowSend);
    } else if (filter === 'receive') {
      result = numbers.filter(m => !m.allowSend);
    } else {
      result = numbers;
    }
    // Ensure the currently selected number remains visible in the list even if filtered out
    if (selected && !result.some(m => m.phoneNumber === selected.phoneNumber)) {
      result = [selected, ...result];
    }

    console.log(`${this.TAG} filteredMessagingNumbers`, result);

    return result;
  });

  // New helpers to avoid arrow functions in the template
  hasSendNumbers = computed(() => this.filteredMessagingNumbers().some(m => m.allowSend));
  hasOtherNumbers = computed(() => this.filteredMessagingNumbers().some(m => !m.allowSend));
  // Hide the "My numbers" label when only "My numbers" are being shown (filter = 'send')
  showMyGroupLabel = computed(() => this.viewNumbersFilter() !== 'send');

  allMessagingNumbers = computed(() => {
    // Copy the array to avoid mutating the signal value within a computed
    const numbers = [...this.messagingService.messagingNumbers()];
    const hasSend = numbers.some(m => m.allowSend);
    if (!hasSend && this.hasAssignUserAccess()) {
      numbers.push({
        phoneNumber: undefined,
        userName: '+ Add New Number',
        badge: undefined,
        allowSend: true,
        campaignId: 'FAKE',
      });
    }

    return numbers;
  });

  selectedMessagingNumber = computed(() => {
    console.log(`${this.TAG} selectedMessagingNumber`, this.messagingService.selectedMessagingNumberSig());
    return this.messagingService.selectedMessagingNumberSig();
  });

  // Notifies parent when "Add New Number" is clicked
  addNumberClicked = output<void>();

  constructor() {
    // Keep the UI filter in sync: if the selected number cannot send and the user hasn't forced a filter,
    // make sure the UI filter is set to 'all'.
    effect(
      () => {
        const selected = this.messagingService.selectedMessagingNumberSig();
        const forced = this.userForcedFilter();
        if (selected && selected.allowSend === false && !forced) {
          this.uiNumbersFilter.set('all');
        }
      },
      { allowSignalWrites: true },
    );
  }

  onNumbersFilterChange(value: 'send' | 'receive' | 'all') {
    this.userForcedFilter.set(true);
    this.uiNumbersFilter.set(value);
  }

  openSupportWidget() {
    this.supportService.toggle();
  }

  onPhoneNumberValueChange(event: SmsPhoneNumberModel) {
    console.log(`${this.TAG} onPhoneNumberValueChange`, event);
    if (event?.campaignId == 'FAKE') {
      this.addNewSmsNumber();
    }

    this.messagingService.selectedMessagingNumber = event;
  }

  addNewSmsNumber() {
    this.addNumberClicked.emit();
  }
}
